import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { TIDS } from "@/constants/testIds";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

export default function WebinarCreate() {
  const nav = useNavigate();
  const [f, setF] = useState({
    title: "",
    description: "",
    scheduled_at: "",
    price: 0,
    duration_min: 60,
    problem_tags: "",
    cover_image: "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // convert datetime-local -> ISO with IST offset
      const iso = new Date(f.scheduled_at).toISOString();
      const body = {
        title: f.title,
        description: f.description,
        scheduled_at: iso,
        price: parseFloat(f.price || 0),
        duration_min: parseInt(f.duration_min || 60),
        cover_image: f.cover_image || null,
        problem_tags: f.problem_tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      const { data } = await api.post("/webinars", body);
      toast.success("Webinar created — auto-drafted templates ready.");
      nav(`/app/webinars/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl vp-fade-up">
      <div className="vp-label">Webinars</div>
      <h1 className="font-display text-3xl font-bold mt-1 mb-6">Create a new webinar</h1>

      <form onSubmit={submit} className="vp-glass p-6 sm:p-8 space-y-5">
        <div>
          <div className="vp-label mb-2">Title</div>
          <input data-testid={TIDS.webinarTitle} className="vp-input" required value={f.title}
            onChange={(e) => set("title", e.target.value)} placeholder="Sell your first cohort in 30 days" />
        </div>
        <div>
          <div className="vp-label mb-2">Description</div>
          <textarea data-testid={TIDS.webinarDesc} className="vp-input" required rows={4} value={f.description}
            onChange={(e) => set("description", e.target.value)} placeholder="What will attendees learn? Who is it for?" />
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <div className="vp-label mb-2">Date & time</div>
            <input data-testid={TIDS.webinarDate} type="datetime-local" required className="vp-input" value={f.scheduled_at}
              onChange={(e) => set("scheduled_at", e.target.value)} />
          </div>
          <div>
            <div className="vp-label mb-2">Duration (min)</div>
            <input type="number" min={15} className="vp-input" value={f.duration_min}
              onChange={(e) => set("duration_min", e.target.value)} />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <div className="vp-label mb-2">Price (₹) — 0 = Free</div>
            <input data-testid={TIDS.webinarPrice} type="number" min={0} className="vp-input" value={f.price}
              onChange={(e) => set("price", e.target.value)} />
          </div>
          <div>
            <div className="vp-label mb-2">Cover image URL (optional)</div>
            <input type="url" className="vp-input" value={f.cover_image}
              onChange={(e) => set("cover_image", e.target.value)} placeholder="https://…" />
          </div>
        </div>
        <div>
          <div className="vp-label mb-2">Problem tags (comma-separated)</div>
          <input data-testid={TIDS.webinarTags} className="vp-input" value={f.problem_tags}
            onChange={(e) => set("problem_tags", e.target.value)} placeholder="low-sales, no-audience, tech-overload" />
        </div>

        <div className="vp-divider" />
        <div className="flex items-center gap-2 text-sm text-[#4A4D57]">
          <Sparkles className="w-4 h-4 text-[#0B5CFF]" />
          A landing page, Zoom meeting and reminder schedule will be created automatically.
        </div>

        <div className="flex gap-3">
          <button type="submit" disabled={saving} data-testid={TIDS.webinarSubmit} className="vp-btn-primary">
            {saving ? "Creating…" : "Create webinar"}
          </button>
          <button type="button" onClick={() => nav(-1)} className="vp-btn-ghost">Cancel</button>
        </div>
      </form>
    </div>
  );
}
